import { useEffect } from "react";

export interface PageMeta {
  title: string;
  description: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogType?: string;
  ogImage?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  keywords?: string;
  noindex?: boolean;
  /** Remove any existing <link rel="canonical"> on mount and restore it on unmount. */
  removeCanonical?: boolean;
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  jsonLdId?: string; // unique id used to clean up the JSON-LD on unmount
}

const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/eyOppnOyy7MNB4iaH3mNvNxg2rH2/social-images/social-1777589791369-OG_TILPrep.webp";

function setMetaTag(name: string, content: string, asProperty = false) {
  const attr = asProperty ? "property" : "name";
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  const previous = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, previous, created };
}

function setLinkTag(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  const previous = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, previous, created };
}

/**
 * Sets per-page document title, meta tags, canonical and (optionally) JSON-LD,
 * and restores the previous values on unmount so values don't bleed across routes.
 */
export function useDocumentMeta(meta: PageMeta) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = meta.title;

    const ogImage = meta.ogImage ?? DEFAULT_OG_IMAGE;
    const tracked: Array<{ el: Element; previous: string | null; created: boolean; attr: string }> = [];

    const track = (
      result: { el: HTMLMetaElement | HTMLLinkElement; previous: string | null; created: boolean },
      attr: string,
    ) => tracked.push({ ...result, attr });

    track(setMetaTag("description", meta.description), "content");
    if (meta.keywords) track(setMetaTag("keywords", meta.keywords), "content");

    track(setMetaTag("og:title", meta.ogTitle ?? meta.title, true), "content");
    track(setMetaTag("og:description", meta.ogDescription ?? meta.description, true), "content");
    track(setMetaTag("og:type", meta.ogType ?? "website", true), "content");
    if (meta.ogUrl) track(setMetaTag("og:url", meta.ogUrl, true), "content");
    track(setMetaTag("og:image", ogImage, true), "content");

    track(setMetaTag("twitter:card", "summary_large_image"), "content");
    track(setMetaTag("twitter:title", meta.twitterTitle ?? meta.ogTitle ?? meta.title), "content");
    track(
      setMetaTag("twitter:description", meta.twitterDescription ?? meta.ogDescription ?? meta.description),
      "content",
    );
    track(setMetaTag("twitter:image", meta.twitterImage ?? ogImage), "content");

    if (meta.noindex) track(setMetaTag("robots", "noindex, nofollow"), "content");

    if (meta.canonical) track(setLinkTag("canonical", meta.canonical), "href");

    let removedCanonical: { el: HTMLLinkElement; href: string } | null = null;
    if (meta.removeCanonical && !meta.canonical) {
      const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (existing) {
        removedCanonical = { el: existing, href: existing.href };
        existing.remove();
      }
    }

    let jsonLdEl: HTMLScriptElement | null = null;
    if (meta.jsonLd) {
      const id = meta.jsonLdId ?? `jsonld-${Math.random().toString(36).slice(2)}`;
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.id = id;
      jsonLdEl.textContent = JSON.stringify(meta.jsonLd);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = previousTitle;
      for (const t of tracked) {
        if (t.created) {
          t.el.remove();
        } else if (t.previous !== null) {
          t.el.setAttribute(t.attr, t.previous);
        }
      }
      if (jsonLdEl) jsonLdEl.remove();
      if (removedCanonical) document.head.appendChild(removedCanonical.el);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default useDocumentMeta;